// Shared helpers + seed data for the Cisco Networking Academy career-path
// pages (CiscoDetailPage.jsx catalog grid, CiscoCareerPathPage.jsx, and
// CiscoCourseDetailPage.jsx). Mirrors the CompTIA cert pattern in
// utils/comptiaCerts.js: one JSON blob on the Program record, parsed here,
// with CMS values always overriding this seed data.

export function slugify(name) {
  return (name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

export function capitalize(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

// Parses the JSON stored in program.ciscoCertTracksSection (repurposed —
// it used to hold the old Cisco Certification Tracks grid, now removed).
// Expects { paths: [{ slug, name, tag, heroIntro, heroImage, badge_image, courses: [...] }] }
export function parseCareerPaths(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.paths)) return parsed.paths;
  } catch {}
  return null;
}

// Seed content so the pages are populated before an admin has entered real
// copy via the CMS. Any field the CMS provides overrides these defaults.
export const CISCO_CAREER_PATHS_SEED = [
  {
    slug: "it-support-specialist",
    name: "IT Support Specialist",
    tag: "Career Path",
    badge_image: "",
    heroImage: "",
    heroIntro:
      "Become a certified IT Support Specialist! Learn to troubleshoot hardware, help users, and build the foundational skills that entry-level IT support and help desk roles need.",
    courses: [
      {
        slug: "computer-hardware-basics",
        name: "Computer Hardware Basics",
        description:
          "Start learning the basics of computer hardware and discover the components of PCs, laptops, and mobile devices.",
        image: "",
        heroImage: "",
        format: "In Class",
        duration: "1 Month",
        hours: "40 Hours",
        fee: "200,000 TZS",
        showCurriculum: true,
        showAchievements: true,
        overviewContent:
          "This course explores the fundamentals of computers and mobile devices — the components that comprise them, how they work, and basic troubleshooting tools and techniques. A great starting point for any IT career.\n\nHere's what you will learn.",
        curriculumModules: ["Course Introduction", "Module 1: Personal Computers", "Module 2: Laptops", "Module 3: Other Mobile Devices", "End of Course Survey", "Final Exam"],
        achievements: [
          { image: "", title: "Computer Hardware Basics", type: "Course Badge", description: "Cisco verifies the earner of this badge successfully completed the Computer Hardware Basics course and achieved this student level credential. The earner has fundamental knowledge of computers and mobile devices, how they work, as well as the basic concepts and skills needed to install components to build, repair, and upgrade personal computers and basic troubleshooting tools and techniques." },
          { image: "", title: "Personal Computers", type: "Achievements", description: "You understand the components, features, and functions of personal computers." },
          { image: "", title: "Laptops", type: "Achievements", description: "You understand the components, features, and functions of laptops." },
          { image: "", title: "Mobile Devices", type: "Achievements", description: "You understand the components, features, and functions of mobile devices such as smartphones, tablets, and wearables." },
        ],
        skills: "Mobile Devices, Device Maintenance, Personal Computers, Laptops",
      },
      {
        slug: "it-customer-support-basics",
        name: "IT Customer Support Basics",
        description:
          "Develop help desk and customer support skills to succeed in entry-level IT positions, managing and troubleshooting IT environments.",
        image: "",
        heroImage: "",
        format: "In Class",
        duration: "1 Month",
        hours: "40 Hours",
        fee: "200,000 TZS",
        showCurriculum: true,
        showAchievements: false,
        overviewContent:
          "This course prepares you for entry-level IT support roles. You'll develop a foundational understanding of help desk operations — effective customer communication, technical troubleshooting, and the use of remote support tools.",
        curriculumModules: ["Course Introduction", "Module 1: Customer Service in IT Support", "Module 2: Processes and Tools for Troubleshooting", "End of Course Survey", "Final Exam"],
        achievements: [],
        skills: "",
      },
    ],
  },
  {
    slug: "junior-cyber-security-analyst",
    name: "Junior Cyber Security Analyst",
    tag: "Career Path",
    badge_image: "",
    heroImage: "",
    heroIntro:
      "Build a career defending organisations from cyber threats — from Cisco's foundational support certification through EC-Council's globally recognised ethical hacking and cybersecurity credentials.",
    courses: [
      {
        slug: "cisco-certified-support-technician",
        name: "Cisco Certified Support Technician",
        description: "The entry point into Cisco certifications — covering the skills needed to diagnose, restore, repair, and support networking and system devices.",
        image: "", heroImage: "",
        format: "In Class", duration: "3 Months", hours: "100 Hours", fee: "750,000 TZS",
        showCurriculum: false, showAchievements: false,
        overviewContent: "Coming soon.", curriculumModules: [], achievements: [], skills: "",
      },
      {
        slug: "cct-certified-cybersecurity-technician",
        name: "CCT — Certified Cybersecurity Technician",
        description: "An EC-Council entry-level certification covering the technical foundations of cybersecurity operations and defence.",
        image: "", heroImage: "",
        format: "Blended", duration: "2 Months", hours: "80 Hours", fee: "800,000 TZS",
        showCurriculum: false, showAchievements: false,
        overviewContent: "Coming soon.", curriculumModules: [], achievements: [], skills: "",
      },
      {
        slug: "ceh-certified-ethical-hacker",
        name: "CEH — Certified Ethical Hacker",
        description: "EC-Council's flagship certification, teaching offensive security skills to identify and fix vulnerabilities before attackers do.",
        image: "", heroImage: "",
        format: "Online", duration: "4 Months", hours: "120 Hours", fee: "1,600,000 TZS",
        showCurriculum: false, showAchievements: false,
        overviewContent: "Coming soon.", curriculumModules: [], achievements: [], skills: "",
      },
      {
        slug: "ehpc-ethical-hacker-penetration-certification",
        name: "EHPC — Ethical Hacker Penetration Certification",
        description: "A hands-on penetration testing certification focused on practical, real-world offensive security skills.",
        image: "", heroImage: "",
        format: "In Class", duration: "2 Months", hours: "80 Hours", fee: "750,000 TZS",
        showCurriculum: false, showAchievements: false,
        overviewContent: "Coming soon.", curriculumModules: [], achievements: [], skills: "",
      },
      {
        slug: "cscu-cyber-security-certified-user",
        name: "CSCU — Cyber Security Certified User",
        description: "An EC-Council foundational certification teaching everyday computer users how to protect themselves online.",
        image: "", heroImage: "",
        format: "In Class", duration: "6 Weeks", hours: "60 Hours", fee: "600,000 TZS",
        showCurriculum: false, showAchievements: false,
        overviewContent: "Coming soon.", curriculumModules: [], achievements: [], skills: "",
      },
    ],
  },
  {
    slug: "data-analytics",
    name: "Data Analytics",
    tag: "Career Path",
    badge_image: "",
    heroImage: "",
    heroIntro:
      "Build essential data analysis skills you can use to solve problems, back up your ideas with data, and make better decisions — with an easy, beginner-friendly approach to Python.",
    courses: [
      {
        slug: "data-science-essentials-python",
        name: "Data Science Essentials with Python",
        description: "Learn the fundamentals of data science using Python — from working with data to building your first analyses.",
        image: "", heroImage: "",
        format: "Blended", duration: "6 Weeks", hours: "60 Hours", fee: "300,000 TZS",
        showCurriculum: false, showAchievements: false,
        overviewContent: "Coming soon.", curriculumModules: [], achievements: [], skills: "",
      },
    ],
  },
  {
    slug: "programming",
    name: "Programming",
    tag: "Career Path",
    badge_image: "",
    heroImage: "",
    heroIntro:
      "Learn Python, JavaScript, and C++ through a structured, three-level pathway — from your first lines of code to advanced, job-ready programming skills.",
    levelHeroImages: { beginner: "", intermediate: "", advanced: "" },
    courses: [
      {
        slug: "beginner-python",
        name: "Python Essentials 1",
        description: "Learn fundamental concepts of computer programming and start building coding skills with the Python programming language.",
        image: "", heroImage: "",
        format: "Blended", duration: "6 Weeks", hours: "30 Hours", fee: "350,000 TZS",
        showCurriculum: false, showAchievements: false,
        overviewContent: "Coming soon.", curriculumModules: [], achievements: [], skills: "",
        level: "beginner", language: "python",
      },
      {
        slug: "beginner-javascript",
        name: "JavaScript Essentials 1",
        description: "Design, develop, and improve JavaScript programs. Boost your programming skills to start your career in technology.",
        image: "", heroImage: "",
        format: "", duration: "", hours: "", fee: "",
        showCurriculum: false, showAchievements: false,
        overviewContent: "Coming soon.", curriculumModules: [], achievements: [], skills: "",
        level: "beginner", language: "javascript",
      },
      {
        slug: "beginner-cpp",
        name: "C++ Essentials 1",
        description: "Build up your C++ programming skills and prepare for entry-level C++ certification.",
        image: "", heroImage: "",
        format: "", duration: "", hours: "", fee: "",
        showCurriculum: false, showAchievements: false,
        overviewContent: "Coming soon.", curriculumModules: [], achievements: [], skills: "",
        level: "beginner", language: "cpp",
      },
      {
        slug: "intermediate-python",
        name: "Python Essentials 2",
        description: "Expand your computer programming skills and get ready to earn a certification.",
        image: "", heroImage: "",
        format: "Blended", duration: "6 Weeks", hours: "40 Hours", fee: "400,000 TZS",
        showCurriculum: false, showAchievements: false,
        overviewContent: "Coming soon.", curriculumModules: [], achievements: [], skills: "",
        level: "intermediate", language: "python",
      },
      {
        slug: "intermediate-javascript",
        name: "JavaScript Essentials 2",
        description: "The second in a two-course series. Gain a strong JavaScript foundation and prepare for certification.",
        image: "", heroImage: "",
        format: "", duration: "", hours: "", fee: "",
        showCurriculum: false, showAchievements: false,
        overviewContent: "Coming soon.", curriculumModules: [], achievements: [], skills: "",
        level: "intermediate", language: "javascript",
      },
      {
        slug: "intermediate-cpp",
        name: "C++ Essentials 2",
        description: "Dive into next-level C++ programming and prepare for associate-level certification.",
        image: "", heroImage: "",
        format: "", duration: "", hours: "", fee: "",
        showCurriculum: false, showAchievements: false,
        overviewContent: "Coming soon.", curriculumModules: [], achievements: [], skills: "",
        level: "intermediate", language: "cpp",
      },
      {
        slug: "advanced-cpp",
        name: "C++ Advanced",
        description: "Master advanced C++ programming techniques and prepare for real-world software development roles.",
        image: "", heroImage: "",
        format: "Blended", duration: "6 Weeks", hours: "40 Hours", fee: "450,000 TZS",
        showCurriculum: false, showAchievements: false,
        overviewContent: "Coming soon.", curriculumModules: [], achievements: [], skills: "",
        level: "advanced", language: "cpp",
      },
    ],
  },
];

// Merge CMS-saved paths over the seed defaults: a path saved via the CMS
// (including its full course list) wins outright; any path not yet touched
// in the CMS falls back to the seed content. Also includes any brand-new
// path added via the CMS's "Add Career Path" button that has no seed
// counterpart at all (e.g. an admin-added "CCNA" path).
export function getCareerPaths(rawJson) {
  const saved = parseCareerPaths(rawJson);
  if (!saved) return CISCO_CAREER_PATHS_SEED;

  const merged = CISCO_CAREER_PATHS_SEED.map((seedPath) => {
    const savedPath = saved.find((p) => p.slug === seedPath.slug);
    if (!savedPath) return seedPath;
    return {
      ...seedPath,
      ...savedPath,
      courses: Array.isArray(savedPath.courses) && savedPath.courses.length > 0
        ? savedPath.courses.map(normalizeCourse)
        : seedPath.courses,
    };
  });

  const seedSlugs = new Set(CISCO_CAREER_PATHS_SEED.map((p) => p.slug));
  const newPaths = saved
    .filter((p) => p.slug && !seedSlugs.has(p.slug))
    .map((p) => ({
      slug: p.slug || "",
      name: p.name || "",
      tag: p.tag || "",
      badge_image: p.badge_image || "",
      heroImage: p.heroImage || "",
      heroIntro: p.heroIntro || "",
      levelHeroImages: p.levelHeroImages || {},
      courses: Array.isArray(p.courses) ? p.courses.map(normalizeCourse) : [],
    }));

  return [...merged, ...newPaths];
}

// Fills in defaults for fields added after older saves (heroImage, duration,
// fee, showCurriculum, showAchievements, achievements, skills), so
// pre-existing saved courses don't silently lose visibility of content they
// already had until re-saved via the CMS.
function normalizeCourse(c) {
  // Older saves had a single `achievementBadge` string instead of an
  // `achievements` array — carry it forward as the course's one badge.
  const achievements = Array.isArray(c.achievements)
    ? c.achievements
    : (c.achievementBadge ? [{ image: c.achievementBadge, title: c.name || "", type: "Course Badge", description: "" }] : []);

  return {
    slug: c.slug || "", name: c.name || "", description: c.description || "",
    image: c.image || "", heroImage: c.heroImage || "",
    format: c.format || "", duration: c.duration || "", hours: c.hours || "", fee: c.fee || "",
    showCurriculum: typeof c.showCurriculum === "boolean" ? c.showCurriculum : Array.isArray(c.curriculumModules) && c.curriculumModules.length > 0,
    showAchievements: typeof c.showAchievements === "boolean" ? c.showAchievements : achievements.length > 0,
    overviewContent: c.overviewContent || "",
    curriculumModules: Array.isArray(c.curriculumModules) ? c.curriculumModules : [],
    achievements,
    skills: c.skills || "",
    // Only meaningful for career paths (currently just "Programming") that
    // group multiple language-specific courses under one level — empty for
    // every other path/course.
    level: c.level || "",
    language: c.language || "",
  };
}

export function findCareerPath(paths, pathSlug) {
  return paths.find((p) => p.slug === pathSlug) ?? null;
}

export function findCourse(path, courseSlug) {
  if (!path) return null;
  return path.courses.find((c) => c.slug === courseSlug) ?? null;
}

// Courses within a path that share a given `level` value (e.g. all the
// "beginner" language variants under the Programming career path).
export function getCoursesForLevel(path, level) {
  if (!path || !level) return [];
  return path.courses.filter((c) => c.level === level);
}

export const LANGUAGE_LABELS = {
  python: "Python",
  javascript: "JavaScript",
  cpp: "C++",
};
